import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import TextWinds from '../../../common/TextWinds';
import HFbutton from '../../../common/input/HFbutton';
import FormRow from '../../../common/input/FormRow';
import FormDialog from '../../../common/FormDialog';
import { Field } from '../../../common/form/Field';
import { LmsStudentType } from '../../../../constants/types/lms-type';
import { StudentStatusEnum } from '../../../../constants/types/student-type';
import {
  checkStudentUid,
  createStudent,
} from '../../../../api/utils/lms/student/api';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert, openModal } from '../../../../store/features/modalSlice';
import NewPasswordModalContent from './NewPasswordModalContent';
import {
  isValidName,
  nameRules,
  uidRules,
} from '../../../../constants/validation';

/** 등록 시점에 받는 항목. 나머지 정보는 학생 상세 화면에서 입력한다. */
type RegisterStudentForm = Pick<LmsStudentType, 'name' | 'uid'>;

interface RegisterStudentDialogProps {
  open: boolean;
  onClose: () => void;
  reloadStudentList: () => void;
}

/**
 * LMS > 학생 관리 > 학생 등록
 *
 * 이름/아이디만 받아 학생을 등록하는 1단계 폼
 * 상세 정보와 보호자 정보는 등록 후 학생 상세 화면에서 영역별로 입력한다.
 */
const RegisterStudentDialog = ({
  open,
  onClose,
  reloadStudentList,
}: RegisterStudentDialogProps) => {
  const dispatch = useAppDispatch();
  const { classId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    trigger,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterStudentForm>({
    defaultValues: { name: '', uid: '' },
  });

  const name = watch('name');
  const uid = watch('uid');
  /** 중복확인을 통과한 아이디. 값으로 들고 있어야 아이디를 고쳤을 때 자동으로 무효가 된다. */
  const [checkedUid, setCheckedUid] = useState<string | null>(null);
  const isUidChecked = !!uid && uid === checkedUid;
  /**
   * 등록 가능 조건. 아직 손대지 않은 항목은 에러가 없어 `formState` 로는 판단할 수 없으므로
   * 값 자체를 직접 본다. 아이디는 중복확인을 통과했다는 것 자체가 형식 검사도 지난 값이다.
   */
  const canSubmit = isValidName(name) && isUidChecked;

  const [isSubmitting, setIsSubmitting] = useState(false);
  /** 중복 요청 방지. 화면 갱신을 기다리지 않고 즉시 막아야 하므로 state 와 별도로 둔다. */
  const submittingRef = useRef(false);

  useEffect(() => {
    // 닫았다 다시 열면 이전 입력이 남지 않도록 초기화한다.
    if (!open) return;

    reset();
    setCheckedUid(null);
  }, [open, reset]);

  /** 아이디 중복확인. 형식 검사를 먼저 하고, 결과는 아이디 항목의 인라인 메시지로 알린다. */
  const checkDuplicate = async () => {
    if (!(await trigger('uid'))) return;

    try {
      const { existUser } = await checkStudentUid({ uid });

      if (existUser) {
        setCheckedUid(null);
        setError(
          'uid',
          { message: '이미 사용 중인 아이디입니다.' },
          { shouldFocus: true },
        );
        return;
      }

      setCheckedUid(uid);
      clearErrors('uid');
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  /** 등록 직후 학생은 대기생 상태이므로 해당 탭으로 옮겨 결과가 바로 보이게 한다. */
  const moveToPendingTab = () => {
    searchParams.set('status', StudentStatusEnum.PENDING);
    setSearchParams(searchParams);
  };

  const submitHandler = async (data: RegisterStudentForm) => {
    if (data.uid !== checkedUid) {
      setError(
        'uid',
        { message: '아이디 중복확인을 해주세요.' },
        { shouldFocus: true },
      );
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await createStudent(classId, {
        name: data.name.trim(),
        uid: data.uid,
      });

      onClose();
      reloadStudentList();
      moveToPendingTab();
      dispatch(
        openModal({
          body: (
            <NewPasswordModalContent
              type="REGIST"
              uid={result.data.uid}
              pw={result.data.password}
            />
          ),
        }),
      );
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title="신규 학생 등록"
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <HFbutton
            onClick={handleSubmit(submitHandler)}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? '등록 중...' : '학생 등록'}
          </HFbutton>
          <HFbutton type="Outlined" onClick={onClose} disabled={isSubmitting}>
            취소
          </HFbutton>
        </div>
      }
    >
      <form onSubmit={handleSubmit(submitHandler)} className="flex flex-col">
        <Field label="학생 이름" error={errors.name?.message}>
          <FormRow
            control={control}
            name="name"
            rules={nameRules({ required: true, label: '학생 이름' })}
            type="text"
            placeholder="학생 이름 입력"
            error={!!errors.name}
          />
        </Field>

        <Field
          label="학생 아이디"
          error={errors.uid?.message}
          success={isUidChecked ? '사용할 수 있는 아이디입니다.' : undefined}
        >
          <FormRow
            control={control}
            name="uid"
            rules={uidRules({ required: true })}
            type="text-button"
            placeholder="5자 이상 20자 이내의 영문, 숫자, _ 입력"
            textButtonProp={{
              text: isUidChecked ? '확인 완료' : '중복확인',
              action: checkDuplicate,
              disabled: isUidChecked,
            }}
            error={!!errors.uid}
          />
        </Field>

        <div className="mt-4 rounded bg-purple0 p-3">
          <TextWinds type="content_body3" color="purple5" className="mb-1">
            학생 등록 안내
          </TextWinds>
          <ul className="ml-4 list-disc space-y-1 text-purple5">
            <li className="text-xs font-normal">
              교사가 이름과 아이디만으로 학생을 간편하게 등록할 수 있어요.
            </li>
            <li className="text-xs font-normal">
              등록한 이름과 아이디는 이후 수정할 수 없어요.
            </li>
            <li className="text-xs font-normal">
              학생은 최초 로그인 시 1회 개인정보를 등록해야 해요.
            </li>
            <li className="text-xs font-normal">
              학생의 상세 정보와 보호자 정보는 학생 등록 후 입력할 수 있어요.
            </li>
          </ul>
        </div>
      </form>
    </FormDialog>
  );
};

export default RegisterStudentDialog;
